import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import type { ParityStrategyIssue } from 'lumen/models';
import {
  STATUS_TRANSLATIONS,
  STATUSES,
} from 'lumen/utils/constants/pi-constant-values';

type CustomOption = Option & {
  value: string;
};

interface ParityTablesCellsTdStatusSelectSignature {
  Args: {
    cellData: {
      issue: ParityStrategyIssue;
      updateStatus: (issue: ParityStrategyIssue, status: string | null) => void;
      value: string;
    };
  };
}

export default class ParityTablesCellsTdStatusSelect extends Component<ParityTablesCellsTdStatusSelectSignature> {
  @service declare intl: Services['intl'];

  get hasStatus(): true | undefined {
    return this.args.cellData.value !== '' || undefined;
  }

  get options(): CustomOption[] {
    const options = Object.values(STATUSES).map((key) => {
      return {
        label: this.intl.t(STATUS_TRANSLATIONS[key]),
        value: key,
      };
    });
    return options;
  }

  get selectedStatus(): CustomOption | null {
    if (this.args.cellData.value === '') {
      return null;
    }

    const translationKey =
      STATUS_TRANSLATIONS[
        this.args.cellData.value as keyof typeof STATUS_TRANSLATIONS
      ];
    const label = translationKey
      ? this.intl.t(translationKey)
      : this.args.cellData.value;
    return { label, value: this.args.cellData.value };
  }

  @action updateStatus(status: CustomOption | null): void {
    // Send the new status value or an empty string if the status is cleared
    this.args.cellData.updateStatus(
      this.args.cellData.issue,
      status?.value ?? '',
    );
  }

  <template>
    <div>
      <PrismSelectInput
        @isSmall={{true}}
        @onChange={{this.updateStatus}}
        @options={{this.options}}
        @placeholder={{t "general.dropdown.select_status"}}
        @unsafe_isClearable={{this.hasStatus}}
        @value={{this.selectedStatus}}
      />
    </div>
  </template>
}
